<template>
    <div>
        <v-snackbar v-model="showSnackbar">
            {{ snackbarText }}
            <template #action="{ attrs }">
                <v-btn text v-bind="attrs" @click="showSnackbar = false">
                    <v-icon>{{ mdiClose }}</v-icon>
                </v-btn>
            </template>
        </v-snackbar>

        <v-navigation-drawer
            v-if="hasResults && filterLayoutCurrent === 'offCanvasRight'"
            v-model="showOffCanvasFilters"
            app
            temporary
            right
            width="400"
            class="d-flex flex-column mb-6 fill-height"
        >
            <div class="ma-5">
                <v-btn tile outlined block large class="font-weight-bold text-center" @click="showOffCanvasFilters = false">
                    {{ $t('closeFilterOverlay') }}
                    <v-spacer />
                    <v-icon right>{{ mdiClose }}</v-icon>
                </v-btn>

                <div class="mt-3">
                    <selected-filters v-model="attrib" :filters="filters" />
                </div>
            </div>

            <filter-vertical
                v-model="attrib"
                :filters="filters"
                :currency-symbol="currencySymbol"
                :analytics-tracker="analyticsTracker"
                :operation="operation"
            />

            <div class="ma-5">
                <v-btn color="primary" block depressed class="px-2 text-none fl-show-results" @click="showOffCanvasFilters = false">
                    {{ $t('guidedShopping.showResults', { RESULT_COUNT: resultMetadata.totalResults }) }}
                </v-btn>

                <v-btn text block class="px-2 mt-2 text-none" @click="resetFilters()">{{ $t('removeFilters') }}</v-btn>
            </div>
        </v-navigation-drawer>

        <v-main v-if="hasResults">
            <template v-if="filterLayoutCurrent === 'offCanvasRight'">
                <v-container fluid>
                    <div class="mb-6">
                        <smart-did-you-mean-info
                            v-if="sdymVariant"
                            :original-query="request.query"
                            :alternative-query="sdymVariant.alternativeQuery"
                            :type="sdymVariant.type"
                            @sdym-clicked="onSdymClicked"
                        ></smart-did-you-mean-info>
                    </div>

                    <!-- TODO: The Template for the different filter layouts are very similar. Merge them somehow to avoid duplicate code. -->
                    <div class="d-flex mb-4">
                        <div class="d-inline-flex">
                            <order v-model="order" :order-from-api="request.order" />
                        </div>
                        <v-spacer />

                        <div class="align-self-center mr-5 hidden-xs-only">
                            <query-info
                                :first="request.first"
                                :count="request.count"
                                :total="resultMetadata.totalResults"
                                :effective-query="resultMetadata.effectiveQuery"
                            />
                        </div>

                        <v-badge
                            :value="numberOfSelectedFilters > 0 && this.$vuetify.breakpoint.xsOnly"
                            :content="numberOfSelectedFilters"
                            overlap
                            color="primary"
                        >
                            <v-btn depressed @click="showOffCanvasFilters = true">
                                <v-icon left>
                                    {{ mdiTune }}
                                </v-icon>
                                {{ $t('doFilter') }}
                            </v-btn>
                        </v-badge>
                    </div>

                    <div class="hidden-sm-and-up text-center mb-3">
                        <query-info
                            :first="request.first"
                            :count="request.count"
                            :total="resultMetadata.totalResults"
                            :effective-query="resultMetadata.effectiveQuery"
                        />
                    </div>

                    <component
                        :is="'Lisa'"
                        v-if="lisaEnabled"
                        :service-id="request.serviceId"
                        :trigger="lisaTrigger"
                        :operation="operation"
                        @open-filters="showOffCanvasFilters = true"
                        v-model="searchApiParams"
                    />

                    <div class="mt-3 mb-6 hidden-xs-only">
                        <selected-filters v-model="attrib" :filters="filters" />
                    </div>

                    <div class="mt-3 mb-6" v-if="resultMetadata.promotion">
                        <promotion
                            :name="resultMetadata.promotion.name"
                            :url="resultMetadata.promotion.url"
                            :image-url="resultMetadata.promotion.imageUrl"
                            :trigger="getMerchandisingTrigger()"
                            :analytics-tracker="analyticsTracker"
                        />
                    </div>

                    <v-row>
                        <v-col v-bind="vuetifyGridProps" v-for="(item, index) in items" class="fl-product-grid" :key="item.id">
                            <product-card
                                :config="config"
                                :query="resultMetadata.effectiveQuery"
                                :id="item.id"
                                :title="item.name"
                                :vendor="getVendor(item)"
                                :summary="summaryStyle === 'full' ? item.summary : ''"
                                :image="item.imageUrl || thumbnailPlaceholderUrl"
                                :show-price="showPrice"
                                :show-price-decimals="showPriceDecimals"
                                :currency-symbol="currencySymbol"
                                :price="item.price"
                                :ordernumber="getOrdernumber(item)"
                                :variants="cart.getNormalizedVariants(item, imageProperties)"
                                :show-cart="showCart"
                                :show-cart-icon="showCartIcon"
                                :cart-text="cartText"
                                :with-amount-input="withAmountInput"
                                :amount-input-min="amountInputMin"
                                :amount-input-max="amountInputMax"
                                :link="item.url"
                                :add-to-cart="addToCart"
                                :badges="getBadgesForItem(item)"
                                :old-price="getOldPrice(item)"
                                :base-price="getBasePrice(item)"
                                :delivery-time="getDeliveryTime(item)"
                                :immediate-delivery="canBeDeliveredImmediately(item)"
                                :additional-info-text="getAdditionalInfoText(item)"
                                :image-orientation="imageOrientation"
                                :gallery-implementation="galleryImplementation"
                                :quick-view-enabled="quickView.enabled"
                                :vendor-style-position="vendorStylePosition"
                                :color-map="colorMap"
                                :image-height="imageHeight"
                                :push-rules="item.pushRules"
                                :product-placement="item.productPlacement"
                                :analytics-tracker="analyticsTracker"
                                :operation="operation"
                                :request-id="resultMetadata.requestId"
                                :result-index="request.first + index"
                                :trigger="getMerchandisingTrigger()"
                                @cart-success="handleCartSuccess"
                                @cart-error="handleCartError"
                            />
                        </v-col>
                    </v-row>

                    <v-divider class="my-6" />

                    <div class="text-center">
                        <paginator
                            :count="request.count"
                            :total="resultMetadata.totalResults"
                            :total-visible="paginationTotalVisible"
                            v-model="first"
                        />
                    </div>
                </v-container>
            </template>
            <template v-else-if="filterLayoutCurrent === 'left'">
                <v-container fluid>
                    <v-row no-gutters>
                        <v-col md="4" lg="3" class="hidden-sm-and-down pr-3">
                            <v-card outlined>
                                <filter-vertical
                                    v-model="attrib"
                                    :filters="filters"
                                    :currency-symbol="currencySymbol"
                                    :analytics-tracker="analyticsTracker"
                                    :operation="operation"
                                />
                            </v-card>
                        </v-col>
                        <v-col cols="12" md="8" lg="9">
                            <component
                                :is="'Lisa'"
                                v-if="lisaEnabled"
                                :service-id="request.serviceId"
                                :trigger="lisaTrigger"
                                :operation="operation"
                                v-model="searchApiParams"
                                hide-express-guide
                            />
                            <div class="mb-6">
                                <smart-did-you-mean-info
                                    v-if="sdymVariant"
                                    :original-query="request.query"
                                    :alternative-query="sdymVariant.alternativeQuery"
                                    :type="sdymVariant.type"
                                    @sdym-clicked="onSdymClicked"
                                ></smart-did-you-mean-info>
                            </div>

                            <div class="d-flex mb-4">
                                <div class="d-inline-flex">
                                    <order v-model="order" :order-from-api="request.order" />
                                </div>
                                <v-spacer />

                                <query-info
                                    :first="request.first"
                                    :count="request.count"
                                    :total="resultMetadata.totalResults"
                                    :effective-query="resultMetadata.effectiveQuery"
                                />
                            </div>

                            <div class="mt-3 mb-6">
                                <selected-filters v-model="attrib" :filters="filters" />
                            </div>

                            <div class="mt-3 mb-6" v-if="resultMetadata.promotion">
                                <promotion
                                    :name="resultMetadata.promotion.name"
                                    :url="resultMetadata.promotion.url"
                                    :image-url="resultMetadata.promotion.imageUrl"
                                    :trigger="getMerchandisingTrigger()"
                                    :analytics-tracker="analyticsTracker"
                                />
                            </div>

                            <v-row>
                                <v-col v-bind="vuetifyGridProps" v-for="(item, index) in items" class="fl-product-grid" :key="item.id">
                                    <product-card
                                        :config="config"
                                        :query="resultMetadata.effectiveQuery"
                                        :id="item.id"
                                        :title="item.name"
                                        :vendor="getVendor(item)"
                                        :summary="summaryStyle === 'full' ? item.summary : ''"
                                        :image="item.imageUrl || thumbnailPlaceholderUrl"
                                        :show-price="showPrice"
                                        :show-price-decimals="showPriceDecimals"
                                        :currency-symbol="currencySymbol"
                                        :price="item.price"
                                        :ordernumber="getOrdernumber(item)"
                                        :variants="cart.getNormalizedVariants(item, imageProperties)"
                                        :show-cart="showCart"
                                        :show-cart-icon="showCartIcon"
                                        :cart-text="cartText"
                                        :with-amount-input="withAmountInput"
                                        :amount-input-min="amountInputMin"
                                        :amount-input-max="amountInputMax"
                                        :link="item.url"
                                        :add-to-cart="addToCart"
                                        :badges="getBadgesForItem(item)"
                                        :old-price="getOldPrice(item)"
                                        :base-price="getBasePrice(item)"
                                        :delivery-time="getDeliveryTime(item)"
                                        :immediate-delivery="canBeDeliveredImmediately(item)"
                                        :additional-info-text="getAdditionalInfoText(item)"
                                        :image-orientation="imageOrientation"
                                        :gallery-implementation="galleryImplementation"
                                        :quick-view-enabled="quickView.enabled"
                                        :vendor-style-position="vendorStylePosition"
                                        :color-map="colorMap"
                                        :image-height="imageHeight"
                                        :push-rules="item.pushRules"
                                        :product-placement="item.productPlacement"
                                        :analytics-tracker="analyticsTracker"
                                        :operation="operation"
                                        :request-id="resultMetadata.requestId"
                                        :result-index="request.first + index"
                                        :trigger="getMerchandisingTrigger()"
                                        @cart-success="handleCartSuccess"
                                        @cart-error="handleCartError"
                                    />
                                </v-col>
                            </v-row>

                            <v-divider class="my-6" />

                            <div class="text-center">
                                <paginator
                                    :count="request.count"
                                    :total="resultMetadata.totalResults"
                                    :total-visible="paginationTotalVisible"
                                    v-model="first"
                                />
                            </div>
                        </v-col>
                    </v-row>
                </v-container>
            </template>
        </v-main>

        <v-main v-else class="ma-6">
            <no-results :query="request.query"></no-results>
        </v-main>
    </div>
</template>

<script lang="ts">
import FilterVertical from './ProductListPage/FilterVertical.vue';
import { mdiChevronLeft, mdiChevronRight, mdiClose, mdiTune } from '@mdi/js';
import { FINDOLOGICSearchAPIJSONResponse10, ParentItem } from '../../../declarations/result';
import { PropType } from 'vue';
import { Operation, Params } from '../api/api';
import { mapActions, mapState } from 'vuex';
import Order from './ProductListPage/Order.vue';
import QueryInfo from './ProductListPage/QueryInfo.vue';
import Paginator from './ProductListPage/Paginator.vue';
import ProductCard from './ProductCard.vue';
import { Badge, CloginJsConfig } from '../../../declarations/config';
import { Vue } from 'vue-property-decorator';
import SmartDidYouMeanInfo from './ProductListPage/SmartDidYouMeanInfo.vue';
import SelectedFilters from './ProductListPage/SelectedFilters.vue';
import NoResults from './ProductListPage/NoResults.vue';
import Promotion from './ProductListPage/Promotion.vue';
import { AddToCartCapable } from '../../Common/Cart/AddToCartCapable';
import { NormalizedVariant } from '../../Common/NormalizedVariant';
import { analyticsTracker as flAnalyticsTracker } from '@findologic/js-common';
import store from '../store';

export default Vue.extend({
    name: 'ProductListPage',
    components: {
        Lisa: () => import('../../Lisa/Lisa.vue'),
        Promotion,
        SelectedFilters,
        SmartDidYouMeanInfo,
        ProductCard,
        Paginator,
        Order,
        FilterVertical,
        QueryInfo,
        NoResults,
    },
    props: {
        items: {
            type: Array as PropType<ParentItem[]>,
            required: true,
        },
        filters: {
            type: Object as PropType<FINDOLOGICSearchAPIJSONResponse10['result']['filters']>,
            required: true,
        },
        params: {
            type: Object as PropType<Params>,
            default() {
                return {} as Params;
            },
        },
        request: {
            type: Object as PropType<FINDOLOGICSearchAPIJSONResponse10['request']>,
            required: true,
        },
        resultMetadata: {
            type: Object as PropType<FINDOLOGICSearchAPIJSONResponse10['result']['metadata']>,
            required: true,
        },
        resultVariant: {
            type: Object as PropType<FINDOLOGICSearchAPIJSONResponse10['result']['variant']>,
            required: true,
        },
        showPrice: {
            type: Boolean,
            required: true,
        },
        showPriceDecimals: {
            type: Boolean,
            required: true,
        },
        currencySymbol: {
            type: String,
            required: true,
        },
        showCart: {
            type: Boolean,
            required: true,
        },
        showCartIcon: {
            type: Boolean,
            required: true,
        },
        cartText: {
            type: String,
            required: true,
        },
        withAmountInput: {
            type: Boolean,
            required: true,
        },
        amountInputMin: {
            type: Number,
            required: true,
        },
        amountInputMax: {
            type: Number,
            required: true,
        },
        showOrdernumber: {
            type: Boolean,
            required: true,
        },
        summaryStyle: {
            type: String as PropType<CloginJsConfig['instantFrontend']['productListingPage']['productCard']['summaryStyle']>,
            required: true,
        },
        filterLayout: {
            type: String as PropType<CloginJsConfig['instantFrontend']['productListingPage']['filtersContainerPosition']>,
            required: true,
        },
        cart: {
            type: Object as PropType<AddToCartCapable>,
            required: true,
        },
        thumbnailPlaceholderUrl: {
            type: String,
            default() {
                return '//cdn.findologic.com/login.symfony/web/images/no_image_640_360.png';
            },
        },
        availableBadges: {
            type: Array as PropType<Badge[]>,
            required: true,
        },
        oldPriceProperty: {
            type: null as PropType<string | null>,
            required: true,
        },
        basePriceProperty: {
            type: null as PropType<string | null>,
            required: true,
        },
        deliveryTimeProperty: {
            type: null as PropType<string | null>,
            required: true,
        },
        immediateDeliveryProperty: {
            type: null as PropType<string | null>,
            required: true,
        },
        additionalInfoTextProperty: {
            type: null as PropType<string | null>,
            required: true,
        },
        grid: {
            type: Object as PropType<CloginJsConfig['instantFrontend']['productListingPage']['grid']>,
            required: true,
        },
        offCanvasFiltersInitiallyOpen: {
            type: Boolean,
            default(): boolean {
                return false;
            },
        },
        lisaEnabled: {
            type: Boolean,
            default: false,
        },
        vendorStylePosition: {
            type: String as PropType<CloginJsConfig['instantFrontend']['productListingPage']['productCard']['vendorStyle']['position']>,
            default: null,
        },
        imageOrientation: {
            type: String as PropType<CloginJsConfig['instantFrontend']['productListingPage']['productCard']['images']['orientation']>,
            required: true,
        },
        galleryImplementation: {
            type: null as PropType<
                CloginJsConfig['instantFrontend']['productListingPage']['productCard']['images']['gallery']['implementation']
            >,
            required: true,
        },
        imageProperties: {
            type: Array as PropType<string[]>,
            required: true,
        },
        quickView: {
            type: Object as PropType<CloginJsConfig['instantFrontend']['productListingPage']['productCard']['quickView']>,
            required: true,
        },
        imageHeight: {
            type: String,
            required: true,
        },
        analyticsTracker: {
            type: Object as PropType<flAnalyticsTracker>,
            required: true,
        },
        config: {
            type: Object as PropType<CloginJsConfig>,
            required: true,
        },
        paginationTotalVisible: {
            type: Number,
            required: true,
        },
    },
    methods: {
        ...mapActions('searchNavigation', ['shallowUpdateParams', 'setSDYM']),
        getVendor(item: ParentItem): string {
            return item.attributes.vendor?.[0] || '';
        },
        getOrdernumber(item: ParentItem): string {
            return (this.showOrdernumber && item.ordernumbers[0]) || '';
        },
        onSdymClicked(alternativeQuery: string): void {
            void this.setSDYM({ alternativeQuery });
        },
        getBadgesForItem(item: ParentItem): Badge[] {
            return this.availableBadges.filter((badge: Badge): boolean => badge.trigger.property in item.properties);
        },
        getOldPrice(item: ParentItem): number {
            if (!this.oldPriceProperty) {
                return null;
            }

            const oldPriceValue = item.properties[this.oldPriceProperty] as string;
            if (!oldPriceValue) {
                return null;
            }

            return parseFloat(oldPriceValue);
        },
        getBasePrice(item: ParentItem): string | null {
            if (!this.basePriceProperty) {
                return null;
            }

            const basePriceValue = item.properties[this.basePriceProperty] as string;

            return basePriceValue || null;
        },
        getDeliveryTime(item: ParentItem): string | null {
            if (!this.deliveryTimeProperty) {
                return null;
            }

            const deliveryTimeValue = item.properties[this.deliveryTimeProperty] as string;
            if (!deliveryTimeValue) {
                return null;
            }

            return deliveryTimeValue;
        },
        canBeDeliveredImmediately(item: ParentItem): boolean {
            if (!this.immediateDeliveryProperty) {
                return false;
            }

            return typeof item.properties[this.immediateDeliveryProperty] !== 'undefined';
        },
        getAdditionalInfoText(item: ParentItem): string | null {
            if (!this.additionalInfoTextProperty) {
                return null;
            }

            const additionalInfoTextValue = item.properties[this.additionalInfoTextProperty] as string;
            if (!additionalInfoTextValue) {
                return null;
            }

            return additionalInfoTextValue;
        },
        resetFilters(): void {
            this.attrib = {};
        },
        handleCartError(): void {
            this.showSnackbar = true;
            this.snackbarText = this.$t('cart.error').toString();
        },
        handleCartSuccess(): void {
            this.showSnackbar = true;
            this.snackbarText = this.$t('cart.success').toString();
        },
        getMerchandisingTrigger(): object {
            return this.operation == Operation.SEARCH
                ? { search_query: this.request.query }
                : { cat: store.getters['searchNavigation/catUrl'] };
        },
    },
    computed: {
        ...mapState('searchNavigation', ['operation']),
        ...mapState(['colorMap']),
        filterLayoutCurrent(): string {
            return this.$vuetify.breakpoint.smAndDown ? 'offCanvasRight' : this.filterLayout;
        },
        searchApiParams: {
            get(): Params {
                return this.params;
            },
            set(params: Params): void {
                void this.shallowUpdateParams(params);
            },
        },
        sdymVariant(): { type: string; alternativeQuery: string } {
            // convert {name: 'sdym', usedType: 'query'} to {type: 'usedType', query: 'query'}
            const resultVariantCopyWithoutName = (({ name, ...sdymField }) => sdymField)(this.resultVariant);
            const usedVariantField = Object.entries(resultVariantCopyWithoutName)[0];

            return usedVariantField
                ? {
                      type: usedVariantField[0],
                      alternativeQuery: usedVariantField[1] as string,
                  }
                : null;
        },
        first: {
            get(): number {
                return this.params.first;
            },
            set(first: number): void {
                void this.shallowUpdateParams({ first });
            },
        },
        order: {
            get(): string {
                return this.params.order;
            },
            set(order: string): void {
                void this.shallowUpdateParams({ order });
            },
        },
        attrib: {
            get(): Params['attrib'] {
                return this.params.attrib;
            },
            set(attrib: Params['attrib']): void {
                void this.shallowUpdateParams({ attrib });
            },
        },
        addToCart(): AddToCartCapable['addToCart'] | undefined {
            // Returning this.cart.addToCart would lead to addToCart being re-bound to the component. That would mean
            // that addToCart would no longer be able to access fields of cart. To prevent that, wrap in a closure.
            return this.cart.supportsAddToCart
                ? async (normalizedVariant: NormalizedVariant, quantity: number = 1) =>
                      await this.cart.addToCart(normalizedVariant, quantity)
                : undefined;
        },
        hasResults(): boolean {
            return this.items.length > 0;
        },
        numberOfSelectedFilters(): number {
            return Object.keys(this.params?.attrib || {}).filter((filterName: string) => filterName !== 'cat_url').length;
        },
        lisaTrigger(): string {
            return this.operation === Operation.NAVIGATION ? this.params.attrib.cat_url[0] : this.resultMetadata.effectiveQuery;
        },
        vuetifyGridProps(): Record<string, number> {
            return {
                // According to the documented Vuetify grid caveats, cols is basically the column configuration for xs,
                // because the xs setting is the base that is valid for all higher screen sizes that are not specified.
                // See https://vuetifyjs.com/en/components/grids/#caveats
                cols: this.grid.xs.colSpan as number,
                sm: this.grid.sm.colSpan as number,
                md: this.grid.md.colSpan as number,
                lg: this.grid.lg.colSpan as number,
            };
        },
    },
    data() {
        return {
            mdiTune,
            mdiClose,
            mdiChevronLeft,
            mdiChevronRight,
            showOffCanvasFilters: this.offCanvasFiltersInitiallyOpen,
            showSnackbar: false,
            snackbarText: '',
        };
    },
});
</script>

<style scoped>
/*
    Shopware 5 adds global box shadows to inputs. That looks bad with Vuetify,
    because Vuetify does its own borders outside of inputs, which mismatch with an
    inset box shadow on the input itself.
 */
>>> input[type='text'] {
    box-shadow: initial;
}
</style>

<style lang="scss">
// For consistency with the shop, inherit its font family rather than using Vuetify's default.
// Make the application selector more specific artificially, so it has precedence over
// very specific Vuetify selectors that specify the font family.
.v-application.v-application.v-application {
    font-family: inherit !important;

    * {
        font-family: inherit !important;
    }
}
</style>
